import React, { CSSProperties, ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { RefreshCcw, Trash2 } from "lucide-react";
import { LogoutOfSystem } from "@utils/helper/index.helper";
import { useNavigate } from "react-router";

/* === interface start === */
interface ErrorBoundProps {
  children: ReactNode;
}
interface StyleSheet {
  [key: string]: CSSProperties;
}
const pageStyle: StyleSheet = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",

    backgroundColor: "#fff",
    padding: "40px",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.1)",
    borderRadius: "12px",
    maxWidth: "500px",
    width: "90%",
    textAlign: "center",
  },
  header: {
    fontSize: "26px",
    textTransform: "uppercase",
    color: "#1da077",
    fontWeight: "bold",
    marginBottom: "16px",
  },
  content: {
    padding: "15px",
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
    color: "#c0392b",
    fontFamily: "monospace",
    margin: "15px 0px",
    fontSize: "14px",
    wordBreak: "break-word",
  },
  button: {
    padding: "8px 14px",
    backgroundColor: "#1da077",
    borderRadius: "6px",
    color: "white",
    cursor: "pointer",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
    border: "none",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontWeight: 500,
    fontSize: "14px",
  },
};
/* === interface end === */

function ErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  const navigate = useNavigate();
  return (
    <div className="flex justify-center items-center bg-[#f3f4f6] h-screen px-4">
      <div role="alert" style={pageStyle.container}>
        <h2 style={pageStyle.header}>Something went wrong</h2>
        <code style={pageStyle.content}>{error.message}</code>
        <div className="flex gap-3 mt-4 flex-wrap justify-center">
          <button
            style={pageStyle.button}
            onClick={() => {
              window.location.reload();
              resetErrorBoundary();
            }}
          >
            <RefreshCcw size={16} /> Try Again
          </button>

          <button
            style={pageStyle.button}
            onClick={() => {
              document.cookie = "";
              sessionStorage.clear();
              localStorage.clear();
              window.location.reload();
              LogoutOfSystem();
              navigate("/login");
            }}
          >
            <Trash2 size={16} /> Clear All Data
          </button>
        </div>
      </div>
    </div>
  );
}

const ErrorBound: React.FC<ErrorBoundProps> = ({ children }) => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onReset={() => {}}>
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBound;
